/**
 * SideSheet.tsx — the New Deal sheet's FRAME, extracted (contact-frame slice
 * 1, RULED 2026-10-06) so the New Contact and Inbound Leads create flows can
 * move onto it. Behaviour and DOM are the New Deal sheet's, byte for byte:
 *
 *   - A floating panel (600px, 12px from the viewport's top, right and
 *     bottom) over a scrim WITHOUT blur. role="dialog", aria-modal, labelled
 *     by its title and described by its subtitle. Tab is trapped inside it
 *     and focus returns to the opener on close (lib/focusTrap.ts).
 *   - The scrim and Esc close it only while it is untouched (isDirty);
 *     otherwise they ask (discardTitle / discardBody). The X is a deliberate
 *     close and does not ask.
 *   - Esc: an OPEN combobox owns its Esc; a nested drawer owns Esc while the
 *     sheet is suspended; Esc inside the confirm answers it ("keep editing").
 *   - Tokens only, inline (no stylesheet): the panel keeps its inline
 *     `animation: slide-in-right …` — the visual suite locates the panel by
 *     div[style*="slide-in-right"].
 *
 * Ids derive from titleId, which ends in "-title": "new-deal-title" gives
 * "new-deal-subtitle" and "new-deal-discard-title".
 *
 * The consumer keeps its fields, drafts and submit; it passes the header's
 * extra row, the footer's contents and the body.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { overlay, radius, shadow, surface } from '../../../../theme/tokens'
import { useFocusTrap } from '../../../../lib/focusTrap'

export interface SideSheetProps {
  /** The title's id; must end in "-title" (the other ids derive from it). */
  titleId: string
  title: string
  subtitle: string
  /** Rendered under the subtitle (the New Deal sheet's "Restore draft?"). */
  headerExtra?: React.ReactNode
  /** The footer bar's contents. */
  footer: React.ReactNode
  onClose: () => void
  /** Read at EVENT time (scrim click, Esc), never during render. */
  isDirty: () => boolean
  /** A nested drawer is up: the trap steps aside and Esc is its own. */
  suspended?: boolean
  discardTitle: string
  discardBody: string
  /** The confirm's destructive button; defaults to "Discard". */
  discardLabel?: string
  /** What the confirm's destructive button does; defaults to onClose. */
  onDiscard?: () => void
  /** The panel's own keydown (not called while the confirm is up). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  children: React.ReactNode
}

export function SideSheet({
  titleId, title, subtitle, headerExtra, footer, onClose, isDirty, suspended = false,
  discardTitle, discardBody, discardLabel = 'Discard', onDiscard, onKeyDown, children,
}: SideSheetProps) {
  const base = titleId.replace(/-title$/, '')
  const subtitleId = `${base}-subtitle`
  const discardTitleId = `${base}-discard-title`
  const sheetRef = useRef<HTMLDivElement>(null)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  // The trap steps aside while a nested drawer (its own portal) is up.
  useFocusTrap(sheetRef, !suspended)
  // The consumer's latest isDirty, for the scrim and Esc (both read it at
  // event time — always after the commit this effect runs in).
  const dirty = useRef(isDirty)
  useLayoutEffect(() => { dirty.current = isDirty })

  // The scrim and Esc: close outright while untouched, ask otherwise.
  function requestClose() {
    if (dirty.current()) setConfirmDiscard(true)
    else onClose()
  }
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // An OPEN combobox owns its Esc (the account picker closes its list):
      // the first Esc must not also close or question the whole sheet. This
      // listener is CAPTURE-phase, so it reads aria-expanded BEFORE the
      // picker's handler runs — in a real browser React flushes a keydown's
      // update synchronously, so a bubble-phase listener on document would
      // already see the list closed (measured: the visual suite's Esc closed
      // the sheet; jsdom did not reproduce the ordering).
      // A nested drawer, opened from here, owns Esc while it is up.
      if (suspended) return
      const t = e.target
      if (t instanceof Element && t.closest('[role="combobox"][aria-expanded="true"]')) return
      // Esc inside the confirm answers it ("keep editing"), never escalates.
      if (confirmDiscard) setConfirmDiscard(false)
      else if (dirty.current()) setConfirmDiscard(true)
      else onClose()
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [confirmDiscard, onClose, suspended])

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim }}
      onClick={e => { if (e.target === e.currentTarget) requestClose() }}>
      <div ref={sheetRef} className="k-drawer" role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={subtitleId}
        onKeyDown={e => { if (!confirmDiscard) onKeyDown?.(e) }}
        style={{
          top: 12, right: 12, bottom: 12, height: 'auto',
          width: 'min(600px, calc(100vw - 24px))',
          borderLeft: 'none', borderRadius: radius.card, overflow: 'hidden',
          background: surface.app, boxShadow: shadow.card,
          animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)',
        }}>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 flex-shrink-0 bg-surface-card border-b border-edge-row">
          <div>
            <h2 id={titleId} className="text-detail-title font-bold text-ink-primary">{title}</h2>
            <p id={subtitleId} className="text-13 text-ink-muted mt-1">{subtitle}</p>
            {headerExtra}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-surface-app text-ink-muted hover:text-ink-primary transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body — the grouped inset list */}
        <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-6">
          {children}
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 px-6 py-4 bg-surface-card border-t border-edge-row flex-shrink-0">
          {footer}
        </div>

        {confirmDiscard && (
          <div className="absolute inset-0 flex items-center justify-center p-6" style={{ background: overlay.scrim }}>
            <div role="alertdialog" aria-modal="true" aria-labelledby={discardTitleId}
              className="k-card w-full max-w-sm p-5 flex flex-col gap-4">
              <div id={discardTitleId} className="text-16 font-semibold text-ink-primary">{discardTitle}</div>
              <p className="text-13 text-ink-muted">{discardBody}</p>
              <div className="flex justify-end gap-3">
                <button type="button" autoFocus onClick={() => setConfirmDiscard(false)} className="k-btn-secondary">Keep editing</button>
                <button type="button" onClick={onDiscard ?? onClose} className="k-btn">{discardLabel}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// One row of a group: the caption-sized label over its control.
export function SheetRow({ label, htmlFor, aside, children }: { label: string; htmlFor?: string; aside?: React.ReactNode; children: React.ReactNode }) {
  const labelEl = htmlFor
    ? <label htmlFor={htmlFor} className="block text-12 font-semibold text-ink-muted mb-1.5">{label}</label>
    : <div className="block text-12 font-semibold text-ink-muted mb-1.5">{label}</div>
  return (
    <div className="px-4 py-3 border-t border-edge-row first:border-t-0">
      {aside ? <div className="flex items-baseline justify-between gap-3">{labelEl}{aside}</div> : labelEl}
      {children}
    </div>
  )
}

// An inline field error (slice 3, F5): under its field, in the danger token.
export function FieldError({ id, text }: { id: string; text: string | null }) {
  if (!text) return null
  return <div id={id} className="text-12 text-danger mt-1">{text}</div>
}

// A group: its caption, then a white card on the tile radius. idBase keeps
// the caption ids unique when one sheet is open over another ("new-contact"
// inside the New Deal sheet); the deal sheet's ids are unchanged.
export function SheetGroup({ caption, idBase = 'new-deal', children }: { caption: string; idBase?: string; children: React.ReactNode }) {
  const id = `${idBase}-group-${caption.toLowerCase().replace(/\s+/g, '-')}`
  return (
    <section aria-labelledby={id}>
      <h3 id={id} className="text-12 font-semibold text-ink-muted px-1 mb-2">{caption}</h3>
      <div className="bg-surface-card rounded-tile">{children}</div>
    </section>
  )
}

// A segmented single choice with radio semantics (stage, priority). Arrow
// keys move the choice, as a radiogroup's do; only the checked option is in
// the Tab order.
export function Segmented<T extends string>({ label, options, value, onChange }: {
  label: string
  options: readonly { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  function move(from: number, dir: 1 | -1) {
    const next = (from + dir + options.length) % options.length
    onChange(options[next].value)
    refs.current[next]?.focus()
  }
  return (
    <div role="radiogroup" aria-label={label} className="flex items-stretch gap-1 p-1 bg-surface-app rounded-control">
      {options.map((o, i) => {
        const checked = o.value === value
        return (
          <button key={o.value || 'none'} type="button" role="radio" aria-checked={checked}
            ref={el => { refs.current[i] = el }}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={e => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(i, 1) }
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(i, -1) }
            }}
            className={`flex-1 px-3 py-1.5 rounded-control text-13 font-semibold transition-colors ${checked ? 'bg-surface-card text-ink-primary shadow-card' : 'text-ink-secondary hover:text-ink-primary'}`}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
